// Elegant minimal symbols for 2120 pricing tiers.
// Stroke-based, dual-color (evergreen + gold), editorial feel.

import React from 'react';

const STROKE = 1.6;

function Compass({ size = 64, accent = 'gold', primary = 'evergreen' }) {
  const acc = accent === 'gold' ? '#D4A95D' : '#163A32';
  const pri = primary === 'gold' ? '#D4A95D' : '#163A32';
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="26" stroke={pri} strokeOpacity="0.18" strokeWidth={STROKE} />
      <circle cx="32" cy="32" r="20" stroke={pri} strokeWidth={STROKE} />
      {/* Tick marks N E S W */}
      <line x1="32" y1="8" x2="32" y2="12" stroke={pri} strokeWidth={STROKE} strokeLinecap="round" />
      <line x1="32" y1="52" x2="32" y2="56" stroke={pri} strokeWidth={STROKE} strokeLinecap="round" />
      <line x1="8" y1="32" x2="12" y2="32" stroke={pri} strokeWidth={STROKE} strokeLinecap="round" />
      <line x1="52" y1="32" x2="56" y2="32" stroke={pri} strokeWidth={STROKE} strokeLinecap="round" />
      {/* Needle */}
      <path d="M32 16 L36 32 L32 48 L28 32 Z" fill={acc} fillOpacity="0.85" stroke={pri} strokeWidth={STROKE * 0.6} strokeLinejoin="round" />
      <circle cx="32" cy="32" r="2" fill={pri} />
    </svg>
  );
}

function Horizon({ size = 64, accent = 'gold', primary = 'evergreen' }) {
  const acc = accent === 'gold' ? '#D4A95D' : '#163A32';
  const pri = primary === 'gold' ? '#D4A95D' : '#163A32';
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      {/* Sky band */}
      <line x1="6" y1="38" x2="58" y2="38" stroke={pri} strokeWidth={STROKE} strokeLinecap="round" />
      {/* Sun */}
      <circle cx="32" cy="38" r="9" fill={acc} fillOpacity="0.18" />
      <circle cx="32" cy="38" r="9" stroke={acc} strokeWidth={STROKE} />
      {/* Rays */}
      <line x1="32" y1="22" x2="32" y2="18" stroke={acc} strokeWidth={STROKE} strokeLinecap="round" />
      <line x1="20" y1="26" x2="17" y2="23" stroke={acc} strokeWidth={STROKE} strokeLinecap="round" />
      <line x1="44" y1="26" x2="47" y2="23" stroke={acc} strokeWidth={STROKE} strokeLinecap="round" />
      {/* Foreground subtle lines (path forward) */}
      <line x1="6" y1="46" x2="58" y2="46" stroke={pri} strokeOpacity="0.35" strokeWidth={STROKE * 0.8} strokeLinecap="round" />
      <line x1="6" y1="52" x2="58" y2="52" stroke={pri} strokeOpacity="0.18" strokeWidth={STROKE * 0.6} strokeLinecap="round" />
    </svg>
  );
}

function Summit({ size = 64, accent = 'gold', primary = 'evergreen' }) {
  const acc = accent === 'gold' ? '#D4A95D' : '#163A32';
  const pri = primary === 'gold' ? '#D4A95D' : '#163A32';
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      {/* Back peak */}
      <path d="M10 48 L26 22 L40 42" stroke={pri} strokeOpacity="0.45" strokeWidth={STROKE} strokeLinejoin="round" strokeLinecap="round" />
      {/* Front peak */}
      <path d="M20 50 L36 24 L54 50 Z" stroke={pri} strokeWidth={STROKE} strokeLinejoin="round" />
      {/* Snowcap */}
      <path d="M30 32 L36 24 L42 32 L38 30 L34 32 Z" fill={acc} fillOpacity="0.85" stroke={pri} strokeWidth={STROKE * 0.6} strokeLinejoin="round" />
      {/* Base ground */}
      <line x1="6" y1="54" x2="58" y2="54" stroke={pri} strokeOpacity="0.35" strokeWidth={STROKE * 0.7} strokeLinecap="round" />
    </svg>
  );
}

export default function PlanSymbol({ symbol, size = 64, highlight = false }) {
  const props = { size, accent: 'gold', primary: highlight ? 'gold' : 'evergreen' };
  if (symbol === 'compass') return <Compass {...props} />;
  if (symbol === 'horizon') return <Horizon {...props} />;
  if (symbol === 'summit') return <Summit {...props} />;
  if (symbol === 'spark')   return <Spark   {...props} />;
  return null;
}

// `Spark`, Free-tier identity: a single seed-of-light radiating gently, in
// keeping with the "Just begin" tagline. Deliberately minimal so the Free row
// reads as foundational, not aspirational.
function Spark({ size, accent, primary }) {
  const pri = primary === 'gold' ? '#B0894C' : '#163A32';
  const acc = '#B0894C'; // gold accent
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* outer ring, quiet aura */}
      <circle cx="32" cy="32" r="22" stroke={pri} strokeOpacity="0.20" strokeWidth={STROKE} />
      {/* four cardinal rays, subtle motion outward */}
      <line x1="32" y1="6"  x2="32" y2="14" stroke={pri} strokeOpacity="0.45" strokeWidth={STROKE * 0.8} strokeLinecap="round" />
      <line x1="32" y1="50" x2="32" y2="58" stroke={pri} strokeOpacity="0.45" strokeWidth={STROKE * 0.8} strokeLinecap="round" />
      <line x1="6"  y1="32" x2="14" y2="32" stroke={pri} strokeOpacity="0.45" strokeWidth={STROKE * 0.8} strokeLinecap="round" />
      <line x1="50" y1="32" x2="58" y2="32" stroke={pri} strokeOpacity="0.45" strokeWidth={STROKE * 0.8} strokeLinecap="round" />
      {/* core, gold seed */}
      <circle cx="32" cy="32" r="6" fill={acc} fillOpacity="0.85" />
      <circle cx="32" cy="32" r="6" stroke={pri} strokeWidth={STROKE * 0.6} />
    </svg>
  );
}
